import { useEffect, useState } from "react";
import { apiFetch } from "@contractor-quoting/api-client";
import { Button, Card } from "@contractor-quoting/shared-ui";

export const tradeOptions = [
  "flooring",
  "drywall",
  "tiling",
  "removal",
  "painting",
  "deck",
  "fence",
  "plumbing",
  "electrical",
  "windows",
  "doors",
  "trim",
] as const;
const offeringModes = ["separately_priced", "included", "optional_attachment", "excluded"] as const;
type OfferingMode = (typeof offeringModes)[number];

interface JobTypeRecord {
  id: string;
  code: string;
  name: string;
  description: string;
  enabled: boolean;
  trades: Array<{
    tradeCode: (typeof tradeOptions)[number];
    required: boolean;
    offeringMode: OfferingMode;
    position: number;
  }>;
}

export function JobTypeManager() {
  const [opened, setOpened] = useState(false);
  const [jobs, setJobs] = useState<JobTypeRecord[]>([]);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [trades, setTrades] = useState<Array<(typeof tradeOptions)[number]>>([]);
  const [tradeModes, setTradeModes] = useState<
    Partial<Record<(typeof tradeOptions)[number], OfferingMode>>
  >({});
  const [error, setError] = useState("");
  const load = async () => {
    try {
      const result = await apiFetch<{ items: JobTypeRecord[] }>("/v1/contractor/job-types");
      setJobs(result.items);
      setError("");
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Customer job types could not be loaded.",
      );
    }
  };
  useEffect(() => {
    if (opened) void load();
  }, [opened]);
  const save = async (job: JobTypeRecord) => {
    try {
      await apiFetch(`/v1/contractor/job-types/${job.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          code: job.code,
          name: job.name,
          description: job.description,
          enabled: job.enabled,
          trades: job.trades.map(({ tradeCode, required, offeringMode }) => ({
            tradeCode,
            required,
            offeringMode,
          })),
        }),
      });
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Customer job type could not be saved.");
    }
  };
  const create = async () => {
    try {
      await apiFetch("/v1/contractor/job-types", {
        method: "POST",
        body: JSON.stringify({
          code,
          name,
          description: "",
          enabled: true,
          trades: trades.map((tradeCode) => ({
            tradeCode,
            required: true,
            offeringMode: tradeModes[tradeCode] ?? "separately_priced",
          })),
        }),
      });
      setName("");
      setCode("");
      setTrades([]);
      setTradeModes({});
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Customer job type could not be created.",
      );
    }
  };
  if (!opened) {
    return (
      <Button className="mt-6" onClick={() => setOpened(true)}>
        Manage customer job types
      </Button>
    );
  }
  return (
    <Card className="mt-6">
      <h2 className="text-xl font-black">Customer-facing job types</h2>
      <p className="mt-2 text-sm text-stone-500">
        Bundle one or more configured trades into the jobs customers can select.
      </p>
      {error ? (
        <p className="mt-3 rounded-xl bg-red-50 p-3 text-sm font-semibold text-red-800">{error}</p>
      ) : null}
      <div className="mt-4 grid gap-3">
        {jobs.map((job, index) => (
          <div
            className="grid gap-3 rounded-xl border border-stone-200 p-4 lg:grid-cols-[1fr_auto]"
            key={job.id}
          >
            <div>
              <input
                aria-label={`Job name ${index + 1}`}
                className="w-full rounded-lg border p-2 font-bold"
                value={job.name}
                onChange={(event) =>
                  setJobs((current) =>
                    current.map((item) =>
                      item.id === job.id ? { ...item, name: event.target.value } : item,
                    ),
                  )
                }
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {job.trades.map((trade) => (
                  <label
                    className="flex items-center gap-2 rounded-lg bg-stone-50 px-2 py-1 text-sm"
                    key={trade.tradeCode}
                  >
                    {trade.tradeCode.replaceAll("_", " ")}
                    <select
                      aria-label={`${trade.tradeCode} offering mode`}
                      className="rounded border bg-white p-1"
                      value={trade.offeringMode}
                      onChange={(event) =>
                        setJobs((current) =>
                          current.map((item) =>
                            item.id === job.id
                              ? {
                                  ...item,
                                  trades: item.trades.map((candidate) =>
                                    candidate.tradeCode === trade.tradeCode
                                      ? {
                                          ...candidate,
                                          offeringMode: event.target.value as OfferingMode,
                                        }
                                      : candidate,
                                  ),
                                }
                              : item,
                          ),
                        )
                      }
                    >
                      {offeringModes.map((mode) => (
                        <option key={mode} value={mode}>
                          {mode.replaceAll("_", " ")}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm font-bold">
                <input
                  type="checkbox"
                  checked={job.enabled}
                  onChange={(event) =>
                    setJobs((current) =>
                      current.map((item) =>
                        item.id === job.id ? { ...item, enabled: event.target.checked } : item,
                      ),
                    )
                  }
                />
                Enabled
              </label>
              <Button onClick={() => void save(job)}>Save</Button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-xl bg-stone-50 p-4">
        <h3 className="font-black">Create a custom bundle</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <input
            className="rounded-lg border p-2"
            placeholder="Job name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <input
            className="rounded-lg border p-2"
            placeholder="job_code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-3">
          {tradeOptions.map((trade) => (
            <div className="flex items-center gap-2 text-sm" key={trade}>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={trades.includes(trade)}
                  onChange={(event) =>
                    setTrades((current) =>
                      event.target.checked
                        ? [...current, trade]
                        : current.filter((item) => item !== trade),
                    )
                  }
                />
                {trade.replaceAll("_", " ")}
              </label>
              {trades.includes(trade) ? (
                <select
                  aria-label={`${trade} new offering mode`}
                  className="rounded border bg-white p-1"
                  value={tradeModes[trade] ?? "separately_priced"}
                  onChange={(event) =>
                    setTradeModes((current) => ({
                      ...current,
                      [trade]: event.target.value as OfferingMode,
                    }))
                  }
                >
                  {offeringModes.map((mode) => (
                    <option key={mode} value={mode}>
                      {mode.replaceAll("_", " ")}
                    </option>
                  ))}
                </select>
              ) : null}
            </div>
          ))}
        </div>
        <Button
          className="mt-4"
          disabled={!name || !code || trades.length === 0}
          onClick={() => void create()}
        >
          Create job type
        </Button>
      </div>
    </Card>
  );
}
